import { clamp, hexToRgb, luminance, mixRgb, smoothstep, type Rgb } from '../../engine/math';
import type { ChromeTone } from '../../engine/types';

// Water colours at the top and bottom of the screen, from the sunlit surface
// down through the twilight zone to the abyss.
const ZONES: Array<{ at: number; top: Rgb; bottom: Rgb }> = [
  { at: 0, top: hexToRgb('#A6E6E6'), bottom: hexToRgb('#4FA6C6') },
  { at: 0.25, top: hexToRgb('#5CBFD4'), bottom: hexToRgb('#1F7AA8') },
  { at: 0.5, top: hexToRgb('#22709E'), bottom: hexToRgb('#0E3F70') },
  { at: 0.75, top: hexToRgb('#0F3A68'), bottom: hexToRgb('#071C44') },
  { at: 1, top: hexToRgb('#081F45'), bottom: hexToRgb('#030B22') },
];

export interface OceanWater {
  top: Rgb;
  bottom: Rgb;
}

/**
 * The water darkens zone by zone as the learner goes deeper. Zones blend linearly: an
 * eased blend is twice as steep mid-zone, which made quick dives darken the whole screen
 * at once. The scene also rate-limits the depth it passes in.
 */
export const oceanWater = (progress: number): OceanWater => {
  const depth = clamp(progress);
  let index = 0;
  while (index < ZONES.length - 2 && depth > ZONES[index + 1].at) {
    index += 1;
  }
  const from = ZONES[index];
  const to = ZONES[index + 1];
  const amount = clamp((depth - from.at) / (to.at - from.at));
  return { top: mixRgb(from.top, to.top, amount), bottom: mixRgb(from.bottom, to.bottom, amount) };
};

/**
 * Dark header text near the bright surface, white once the water darkens. The
 * header sits at the top of the screen, so the top colour counts most.
 */
export const oceanChromeTone = (progress: number): ChromeTone =>
  oceanChromeLuminance(progress) > CHROME_THRESHOLD ? 'dark' : 'light';

const CHROME_THRESHOLD = 0.31;

/** Top-weighted luminance of the water behind the header. */
export const oceanChromeLuminance = (progress: number) => {
  const { top, bottom } = oceanWater(progress);
  return luminance(top) * 0.7 + luminance(bottom) * 0.3;
};

/**
 * 1 where dark text reads best, 0 where white does, blended across the switch
 * so in-scene labels change colour gradually rather than in one frame.
 */
export const oceanInkAmount = (progress: number) =>
  smoothstep(CHROME_THRESHOLD - 0.06, CHROME_THRESHOLD + 0.06, oceanChromeLuminance(progress));
